<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * The one count chip every sidebar row uses - Conversations, Tickets,
 * Channels, labels, teams, folders alike. It has no variants on purpose: a
 * sidebar where some counts shout is a sidebar where the shouting is noise.
 * Red is reserved for the two places that must interrupt, and neither is a
 * sidebar row: the notification bell and the rail's Inbox badge paint
 * themselves.
 */
const props = defineProps({
  count: { type: [Number, String], default: 0 },
  // Most rows count unread items, but some count a whole queue or backlog.
  // The digits look the same either way; only the announced name differs.
  labelKey: { type: String, default: 'SIDEBAR.UNREAD_COUNT' },
});

const { t } = useI18n();

const normalizedCount = computed(() => {
  const count = Number(props.count);
  return Number.isFinite(count) && count > 0 ? count : 0;
});

const displayCount = computed(() =>
  normalizedCount.value > 99 ? '99+' : String(normalizedCount.value)
);

// The digits alone read as part of the row's label; the badge names what they
// count so a screen reader announces "Mentions, 3 unread".
const countLabel = computed(() =>
  t(props.labelKey, { count: normalizedCount.value })
);
</script>

<template>
  <span
    v-if="normalizedCount > 0"
    data-test-id="sidebar-unread-badge"
    role="status"
    :aria-label="countLabel"
    class="inline-block flex-shrink-0 text-end text-xs leading-none tabular-nums text-n-slate-10"
  >
    {{ displayCount }}
  </span>
  <span v-else class="hidden" />
</template>
